import React from 'react';
import { FileText, Edit, Download, Trash2 } from 'lucide-react';
import { Template } from './types';

interface TemplateCardProps {
    template: Template;
    onFill: (template: Template) => void;
    onDownload: (template: Template) => void;
    onDelete: (id: string) => void;
    formatDate: (dateString: string) => string;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
    template,
    onFill,
    onDownload,
    onDelete,
    formatDate,
}) => {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className="bg-blue-50 p-2.5 rounded-lg flex-shrink-0">
                        <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                            <h3 className="text-base font-semibold text-gray-900 truncate">
                                {template.name}
                            </h3>
                            {template.category && (
                                <span
                                    className="px-2 py-0.5 text-xs font-medium text-white rounded-full whitespace-nowrap"
                                    style={{ backgroundColor: template.category.color }}
                                >
                                    {template.category.name}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                            {template.description || 'Yeni yüklenen şablon'}
                        </p>
                    </div>
                    <div className="flex items-center flex-shrink-0 gap-0.5">
                        <button
                            onClick={() => onFill(template)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Doldur"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onDownload(template)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="İndir"
                        >
                            <Download className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onDelete(template.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Sil"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="text-gray-500">Dosya</div>
                    <div
                        className="font-medium text-gray-900 text-right truncate"
                        title={template.original_filename}
                    >
                        {template.original_filename}
                    </div>

                    <div className="text-gray-500">Boyut</div>
                    <div className="font-medium text-gray-900 text-right">
                        {Math.round(template.file_size / 1024)} KB
                    </div>

                    <div className="text-gray-500">Yükleme</div>
                    <div className="font-medium text-gray-900 text-right">
                        {formatDate(template.created_at)}
                    </div>

                    <div className="text-gray-500">Güncelleme</div>
                    <div className="font-medium text-gray-900 text-right">
                        {formatDate(template.updated_at)}
                    </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mb-2">
                        Alanlar ({(template.Keys || []).length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {(template.Keys || []).slice(0, 3).map((key, index) => (
                            <span
                                key={index}
                                className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                            >
                                {key.key_name}
                            </span>
                        ))}
                        {(template.Keys || []).length > 3 && (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                                +{(template.Keys || []).length - 3} daha
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <button
                    onClick={() => onFill(template)}
                    className="w-full bg-blue-900 text-white py-2.5 px-4 rounded-lg hover:bg-blue-800 transition-colors flex items-center justify-center font-medium text-sm"
                >
                    <Edit className="h-4 w-4 mr-2" />
                    Doldur ve İndir
                </button>
            </div>
        </div>
    );
};

export default TemplateCard;
